import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { Grid, Stack, Typography } from "@mui/material";
import React, { FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { JobFormTitle } from "@/features/job/components/job-form/JobFormTitle";
import { TJobForm } from "@/features/job/types/JobForm";
import { HourMinuteAmountInput } from "@/features/job/components/HourMinuteAmountInput";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { black, blue } from "@/theme/colors";
import { ClearIcon } from "@mui/x-date-pickers";
import { getCircledNumber } from "@/utils/number";
import { initJobWorkingTimeForm } from "@/features/job/utils/convertJobFormData";

type TJobWorkingConditionProps = {
  isDraft?: boolean;
};

export const JobWorkingCondition: FC<TJobWorkingConditionProps> = ({ isDraft }) => {
  const methods = useFormContext<TJobForm>();
  const { control, setValue } = methods;

  const jobWorkingTimesWatchField = useWatch({
    control,
    name: "jobWorkingTimes",
  });

  return (
    <Stack gap={2}>
      <InnerBox title={<JobFormTitle text="勤務条件" /> } showDivider={false}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormField control={control} name="workplace" required={!isDraft}>
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="workplaceDetail">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          {(jobWorkingTimesWatchField ?? []).map((workingTime, index) => (
            <React.Fragment key={index}>
              <Grid item xs={6} id={`jobWorkingTimes-${index}-workingStartMinute`}>
                <FormField
                  control={control}
                  label={`始業${getCircledNumber(index + 1)}`}
                  id={`jobWorkingTimes-${index}-workingStartHour`}
                  name={`jobWorkingTimes.${index}.workingStartHour`}
                  errorMessage={
                    methods.getFieldState(`jobWorkingTimes.${index}.workingStartHour`).error?.message ||
                    methods.getFieldState(`jobWorkingTimes.${index}.workingStartMinute`).error?.message
                  }
                  required={!isDraft}
                >
                  <HourMinuteAmountInput
                    readonly={false}
                    isError={
                      !!methods.getFieldState(`jobWorkingTimes.${index}.workingStartHour`).error ||
                      !!methods.getFieldState(`jobWorkingTimes.${index}.workingStartMinute`).error
                    }
                    nameHour={`jobWorkingTimes.${index}.workingStartHour`}
                    nameMinute={`jobWorkingTimes.${index}.workingStartMinute`}
                  />
                </FormField>
              </Grid>
              <Grid item xs={6} id={`jobWorkingTimes-${index}-workingEndMinute`}>
                <FormField
                  control={control}
                  label={`終業${getCircledNumber(index + 1)}`}
                  id={`jobWorkingTimes-${index}-workingEndHour`}
                  name={`jobWorkingTimes.${index}.workingEndHour`}
                  errorMessage={
                    methods.getFieldState(`jobWorkingTimes.${index}.workingEndHour`).error?.message ||
                    methods.getFieldState(`jobWorkingTimes.${index}.workingEndMinute`).error?.message
                  }
                  required={!isDraft}
                >
                  <HourMinuteAmountInput
                    readonly={false}
                    isError={
                      !!methods.getFieldState(`jobWorkingTimes.${index}.workingEndHour`).error ||
                      !!methods.getFieldState(`jobWorkingTimes.${index}.workingEndMinute`).error
                    }
                    nameHour={`jobWorkingTimes.${index}.workingEndHour`}
                    nameMinute={`jobWorkingTimes.${index}.workingEndMinute`}
                  />
                </FormField>
              </Grid>
            </React.Fragment>
          ))}
          {jobWorkingTimesWatchField && (
            <Grid paddingTop="0 !important" item xs={12} mt={1}>
              <Stack flexDirection="row" alignItems="center" gap={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    setValue("jobWorkingTimes", [
                      ...jobWorkingTimesWatchField,
                      { ...initJobWorkingTimeForm },
                    ]);
                  }}
                >
                  <AddCircleOutlineIcon
                    sx={{
                      color: blue[80],
                      fontSize: 18,
                    }}
                  />
                  <Typography variant="cap12">始業・終業を追加</Typography>
                </Stack>
                {jobWorkingTimesWatchField?.length && jobWorkingTimesWatchField.length > 1 && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ cursor: "pointer" }}
                    // onClick={handleRemoveField}
                    onClick={() => {
                      const newSchedules = [...jobWorkingTimesWatchField];
                      newSchedules.pop();
                      setValue("jobWorkingTimes", newSchedules);
                    }}
                  >
                    <ClearIcon sx={{ color: black[80], fontSize: 18 }} />
                    <Typography variant="cap12Light">削除</Typography>
                  </Stack>
                )}
              </Stack>
            </Grid>
          )}
          <Grid item xs={6}>
            <FormField control={control} name="regularHolidays">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="irregularHolidays">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="totalAnnualHolidays">
              <Input placeholder="入力してください" commasFormat={false} decimalPlaces={1} endAdornment="日" />
            </FormField>
          </Grid>
        </Grid>
      </InnerBox>
    </Stack>
  );
};
